<template>
  <q-dialog v-model="isOpen" position="top">
    <q-card
      class="bg-white"
      style="width: 100%; border-top-left-radius: 16px; border-top-right-radius: 16px"
    >
      <div class="row justify-between items-center q-px-md q-pt-md q-pb-sm border-bottom">
        <div class="text-subtitle1 text-weight-bold text-primary text-uppercase">
          Manual Adjustment
        </div>
        <q-btn flat round dense icon="close" v-close-popup @click="resetForm" />
      </div>

      <q-card-section class="q-py-sm">
        <div class="text-caption text-grey-7 q-mb-md">
          <q-icon name="warning" color="warning" size="xs" class="q-mr-xs" />
          Adjustments are logged separately from play-by-play data and will show as a gap in
          detailed stats for this span.
        </div>

        <!-- SCOPE SELECT -->
        <div class="text-caption text-weight-bold text-grey-8 q-mb-xs">What needs fixing?</div>
        <div class="row q-gutter-xs q-mb-md">
          <q-btn
            v-for="opt in scopeOptions"
            :key="opt.value"
            :label="opt.label"
            :color="scope === opt.value ? 'primary' : 'grey-2'"
            :text-color="scope === opt.value ? 'white' : 'black'"
            unelevated
            size="sm"
            class="col-grow text-weight-bold"
            @click="selectScope(opt.value)"
          />
        </div>

        <!-- SCORE ADJUSTMENT -->
        <div v-if="scope === 'score'">
          <div class="text-caption text-weight-bold text-grey-8 q-mb-xs">Team</div>
          <q-btn-toggle
            v-model="scoreHalfInning"
            spread
            no-caps
            unelevated
            toggle-color="primary"
            color="grey-2"
            text-color="grey-9"
            :options="[
              { label: gameStore.visitingTeamName, value: 'top' },
              { label: gameStore.homeTeamName, value: 'bottom' },
            ]"
            class="q-mb-md"
          />

          <div class="text-caption text-weight-bold text-grey-8 q-mb-xs">Inning</div>
          <q-input
            v-model.number="scoreInning"
            type="number"
            dense
            outlined
            min="1"
            class="q-mb-md"
            style="max-width: 120px"
          />

          <div class="text-caption text-weight-bold text-grey-8 q-mb-xs">Run Change</div>
          <div class="row q-gutter-sm q-mb-md items-center">
            <q-btn
              icon="remove"
              round
              unelevated
              color="negative"
              @click="scoreDelta = Math.max(scoreDelta - 1, -20)"
            />
            <div class="text-h5 text-weight-bold" style="min-width: 48px; text-align: center">
              {{ scoreDelta > 0 ? '+' : '' }}{{ scoreDelta }}
            </div>
            <q-btn
              icon="add"
              round
              unelevated
              color="positive"
              @click="scoreDelta = Math.min(scoreDelta + 1, 20)"
            />
          </div>
        </div>

        <!-- INNING JUMP -->
        <div v-if="scope === 'inning'">
          <div class="text-caption text-weight-bold text-grey-8 q-mb-xs">Half</div>
          <q-btn-toggle
            v-model="jumpHalfInning"
            spread
            no-caps
            unelevated
            toggle-color="primary"
            color="grey-2"
            text-color="grey-9"
            :options="[
              { label: 'Top', value: 'top' },
              { label: 'Bottom', value: 'bottom' },
            ]"
            class="q-mb-md"
          />

          <div class="text-caption text-weight-bold text-grey-8 q-mb-xs">Inning</div>
          <q-input
            v-model.number="jumpInning"
            type="number"
            dense
            outlined
            min="1"
            style="max-width: 120px"
          />
        </div>

        <!-- LINEUP SPOT JUMP -->
        <div v-if="scope === 'lineup_spot'">
          <div class="text-caption text-weight-bold text-grey-8 q-mb-xs">
            Set Current Batter ({{ jumpLineupTeamLabel }})
          </div>
          <q-select
            v-model="jumpBatterId"
            :options="jumpLineupOptions"
            option-label="label"
            option-value="id"
            emit-value
            map-options
            dense
            outlined
            label="Batter"
          />
        </div>

        <!-- REASON (always available, optional) -->
        <div v-if="scope" class="q-mt-md">
          <div class="text-caption text-weight-bold text-grey-8 q-mb-xs">
            Reason <span class="text-grey-5">(optional)</span>
          </div>
          <q-input
            v-model="reason"
            dense
            outlined
            placeholder="e.g. lost track during a long inning"
          />
        </div>
      </q-card-section>

      <q-separator v-if="scope" />

      <q-card-actions v-if="scope" class="q-pa-md">
        <q-btn
          unelevated
          color="primary"
          label="Apply Adjustment"
          class="full-width"
          :loading="isApplying"
          :disable="!canApply || isApplying"
        >
          <!-- FIX: Removed no-parent-event so the button click opens the menu natively -->
          <q-menu anchor="top middle" self="bottom middle">
            <q-card class="q-pa-sm" style="min-width: 220px">
              <div class="text-caption q-mb-sm">
                Apply this adjustment? It will be logged and can't be automatically undone.
              </div>
              <div class="row justify-end q-gutter-xs">
                <q-btn flat dense label="Cancel" color="grey-7" v-close-popup />
                <q-btn
                  flat
                  dense
                  label="Apply"
                  color="primary"
                  v-close-popup
                  @click="applyAdjustment"
                />
              </div>
            </q-card>
          </q-menu>
        </q-btn>
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useGameStore } from 'stores/game-store'
import { useAsyncGuard } from 'src/composables/useAsyncGuard'
import { useNotify } from 'src/helpers/notify'
import type { HalfInning } from 'src/stores/base-types'

const isOpen = defineModel<boolean>({ default: false })
const gameStore = useGameStore()

type AdjustmentScope = 'score' | 'inning' | 'lineup_spot'

const scopeOptions: { label: string; value: AdjustmentScope }[] = [
  { label: 'Score', value: 'score' },
  { label: 'Inning', value: 'inning' },
  { label: 'Lineup Spot', value: 'lineup_spot' },
]

const scope = ref<AdjustmentScope | ''>('')
const reason = ref('')

// --- SCORE STATE ---
const scoreHalfInning = ref<HalfInning>('top')
const scoreInning = ref<number>(gameStore.activeGameSummary?.currentInning ?? 1)
const scoreDelta = ref<number>(0)

// --- INNING JUMP STATE ---
const jumpHalfInning = ref<HalfInning>('top')
const jumpInning = ref<number>(gameStore.activeGameSummary?.currentInning ?? 1)

// --- LINEUP SPOT STATE ---
const jumpBatterId = ref<number | null>(null)
const jumpLineupTeamLabel = computed(() =>
  gameStore.activeGameSummary?.currentHalfInning === 'top'
    ? gameStore.visitingTeamName
    : gameStore.homeTeamName,
)
const jumpLineupOptions = computed(() => {
  const lineup = gameStore.activeOffensiveLineup
  return lineup.map((entry) => ({
    id: entry.player.id,
    label: `#${entry.player.uniformNumber || '-'} ${entry.player.name} (${entry.battingOrder})`,
  }))
})

const selectScope = (s: AdjustmentScope) => {
  scope.value = s
}

const canApply = computed(() => {
  if (scope.value === 'score') return scoreDelta.value !== 0 && scoreInning.value > 0
  if (scope.value === 'inning') return jumpInning.value > 0
  if (scope.value === 'lineup_spot') return jumpBatterId.value !== null
  return false
})

const resetForm = () => {
  scope.value = ''
  reason.value = ''
  scoreDelta.value = 0
  jumpBatterId.value = null
}

const { isPending: isApplying, guard: guardApply } = useAsyncGuard()
const applyAdjustment = guardApply(async () => {
  if (scope.value === 'score') {
    await gameStore.adjustScore(
      scoreHalfInning.value,
      scoreInning.value - 1, // stored as 0-indexed array
      scoreDelta.value,
      reason.value || undefined,
    )
    useNotify('Score adjusted', 1000, 'positive')
  } else if (scope.value === 'inning') {
    await gameStore.jumpToInning(jumpInning.value, jumpHalfInning.value, reason.value || undefined)
    useNotify('Inning updated', 1000, 'positive')
  } else if (scope.value === 'lineup_spot') {
    if (jumpBatterId.value !== null) {
      await gameStore.jumpToLineupSpot(jumpBatterId.value, reason.value || undefined)
      useNotify('Batter updated', 1000, 'positive')
    }
  }
  resetForm()
  isOpen.value = false
})


</script>
